import { extender } from "@shared/backend/class-extender";
import HyperEntity from "@shared/backend/hyper-entity.decorator";
import { JsonField } from "@shared/helpers/custom-fields";
import {
  partnerOrganizationDailyTargetReachedSql,
  partnerOrganizationTodayApprovedSeatsSql,
} from "@shared/helpers/daily-volunteer-target";
import { Roles } from "@shared/Roles";
import { Fields, Relations } from "remult";
import { CommunicationRecord } from "../communication-record";
import { OrganizationManager } from "../organization-manager";
import { Opportunity } from "../opportunity";
import { PartnerOrganizationAddress } from "../partner-organization-address";
import { PartnerOrganizationManager } from "../partner-organization-manager";
import { User } from "../user";
import { PartnerOrganizationEntityConfig } from "./config";
import { PartnerOrganizationFilters } from "./filters";
import { PartnerOrganizationMethods } from "./methods";

@HyperEntity("partner-organization", PartnerOrganizationEntityConfig)
export class PartnerOrganization extends extender(
  PartnerOrganizationFilters,
  PartnerOrganizationMethods,
) {
  @Fields.id()
  id = "";

  @Fields.string({
    dbName: "parent_id",
    allowNull: true,
    caption: "ארגון אב",
  })
  parentId?: string | null;

  @Relations.toOne<PartnerOrganization, PartnerOrganization>(
    () => PartnerOrganization,
    "parentId",
  )
  parent?: PartnerOrganization;

  @Relations.toMany(() => PartnerOrganization, {
    field: "parentId",
    caption: "תתי ארגונים",
  })
  children?: PartnerOrganization[];

  @Fields.string({ caption: "שם" })
  name = "";

  @Fields.string({ allowNull: true, caption: "תיאור" })
  description = "";

  @Fields.boolean({ dbName: "accepts_groups", caption: "קולט קבוצות" })
  acceptsGroups = false;

  @Fields.boolean({
    dbName: "accepts_english_speakers",
    caption: "דוברי אנגלית",
  })
  acceptsEnglishSpeakers = false;

  @Fields.string({
    dbName: "logo_image_id",
    allowNull: true,
    caption: "לוגו",
  })
  logoImageId = "";

  @Fields.string({ allowNull: true, caption: "כתובת" })
  address = "";

  @JsonField<PartnerOrganization, { lat: number; lng: number }>({
    allowedKeys: ["lat", "lng"],
    allowNull: true,
  })
  coordinates?: { lat: number; lng: number } | null;

  @Fields.integer({
    dbName: "max_group_size",
    allowNull: true,
    caption: "מקסימום אנשים בקבוצה",
  })
  maxGroupSize?: number | null;

  @Fields.string({
    dbName: "operating_hours",
    allowNull: true,
    caption: "שעות פעילות",
  })
  operatingHours = "";

  @Fields.boolean({ dbName: "is_accessible", caption: "נגישות" })
  isAccessible = false;

  @Fields.integer({
    dbName: "cooperation_level",
    allowNull: true,
    caption: "מידת שיתוף פעולה",
  })
  cooperationLevel?: number | null;

  @Fields.integer({
    dbName: "daily_volunteer_target",
    allowNull: true,
    caption: "יעד מתנדבים יומי",
  })
  dailyVolunteerTarget?: number | null;

  @Fields.integer({
    allowApiUpdate: false,
    caption: "מתנדבים שאושרו היום",
    sqlExpression: () =>
      partnerOrganizationTodayApprovedSeatsSql(`"partner-organization"."id"`),
  })
  todayApprovedSeats = 0;

  @Fields.boolean({
    allowApiUpdate: false,
    caption: "יעד יומי הושלם",
    sqlExpression: () => partnerOrganizationDailyTargetReachedSql(),
  })
  dailyVolunteerTargetReached = false;

  @Fields.literal(() => ["CALL_CENTER", "COORDINATORS"] as const, {
    dbName: "handled_by",
    allowNull: true,
    caption: "מטפל",
  })
  handledBy?: "CALL_CENTER" | "COORDINATORS" | null;

  @Fields.string({
    dbName: "handling_manager_id",
    allowNull: true,
    caption: "מנהל אחראי",
  })
  handlingManagerId?: string | null;

  @Relations.toOne<PartnerOrganization, OrganizationManager>(
    () => OrganizationManager,
    "handlingManagerId",
  )
  handlingManager?: OrganizationManager;

  @Fields.date({
    dbName: "next_follow_up_at",
    allowNull: true,
    caption: "לחזור בתאריך",
  })
  nextFollowUpAt?: Date | null;

  @Fields.string({
    includeInApi: Roles.AdminsAndManager,
    caption: "הערות פנימיות",
  })
  comments = "";

  @Relations.toMany(() => User, {
    field: "partnerOrganizationId",
    caption: "מנהלי אפליקציה",
  })
  managers: User[] = [];

  @Relations.toMany(() => PartnerOrganizationManager, {
    field: "partnerOrganizationId",
    caption: "מנהלים ארגוניים",
  })
  organizationManagers: PartnerOrganizationManager[] = [];

  @Relations.toMany(() => Opportunity, {
    field: "partnerOrganizationId",
    caption: "משימות",
  })
  opportunities: Opportunity[] = [];

  @Relations.toMany(() => PartnerOrganizationAddress, {
    field: "partnerOrganizationId",
    caption: "כתובות",
  })
  addresses: PartnerOrganizationAddress[] = [];

  @Relations.toMany(() => CommunicationRecord, "partnerOrganizationId")
  communicationRecords?: CommunicationRecord[];

  @Fields.createdAt({ dbName: "created_at", caption: "תאריך יצירה" })
  createdAt = new Date();

  @Fields.updatedAt({ dbName: "updated_at", caption: "תאריך עדכון" })
  updatedAt = new Date();
}
